// useCustomerCategory.ts
import { 
    useGetAllCustomerTypes as useGetAllCustomerTypesOrval, 
    useGetCustomerType as useGetCustomerTypeOrval,
    useCreateCustomerType as useCreateCustomerTypeOrval,
    useUpdateCustomerType as useUpdateCustomerTypeOrval,
    useDeleteCustomerType as useDeleteCustomerTypeOrval
} from '@/shared/api/client/customer-types';
import { CustomerTypeDTO } from '@/shared/api/model';
import { UseQueryResult, useQueryClient } from "@tanstack/react-query";

export function useGetAllCustomerCategories() {
    const query = useGetAllCustomerTypesOrval({
        query: {
            staleTime: 60 * 1000, // Keep data fresh for 1 minute
        }
    });
    
    // Extract data from nested axios response and maintain the original interface
    // @ts-ignore
    return {
        ...query,
        data: query.data,
        error: query.error as Error
    } as UseQueryResult<CustomerTypeDTO[], Error>;
}

export function useGetCustomerCategory(code: string) {
    const query = useGetCustomerTypeOrval(code, {
        query: {
            enabled: !!code,
            staleTime: 60 * 1000,
        }
    });
    
    // Extract data from nested axios response and maintain the original interface
    return {
        ...query,
        data: query.data,
        error: query.error as Error
    } as UseQueryResult<CustomerTypeDTO, Error>;
}

export function useCreateCustomerCategory() {
    const queryClient = useQueryClient();
    
    const mutation = useCreateCustomerTypeOrval({
        mutation: {
            onSuccess: () => {
                // Invalidate and refetch
                queryClient.invalidateQueries({queryKey: ['customerTypes']});
            },
        }
    });
    
    // Maintain backward compatibility
    return {
        ...mutation,
        mutateAsync: (customerCategory: CustomerTypeDTO) => 
            mutation.mutateAsync({ data: customerCategory })
                .then(response => response)
    };
}

export function useUpdateCustomerCategory() {
    const queryClient = useQueryClient();
    
    const mutation = useUpdateCustomerTypeOrval({
        mutation: {
            onSuccess: () => {
                // Invalidate and refetch
                queryClient.invalidateQueries({queryKey: ['customerTypes']});
            },
        }
    });
    
    // Maintain backward compatibility
    return {
        ...mutation,
        mutateAsync: (customerCategory: CustomerTypeDTO) => 
            mutation.mutateAsync({ data: customerCategory })
                .then(response => response)
    };
}

export function useDeleteCustomerCategory() {
    const queryClient = useQueryClient();
    
    const mutation = useDeleteCustomerTypeOrval({
        mutation: {
            onSuccess: () => {
                // Invalidate and refetch
                queryClient.invalidateQueries({queryKey: ['customerTypes']});
            },
        }
    });
    
    // Maintain backward compatibility
    return {
        ...mutation,
        mutateAsync: (code: string) => 
            mutation.mutateAsync({ code })
                .then(response => response)
    };
}

// Add backward compatibility exports for components still using the old names
export {
    // @ts-ignore
    useGetAllCustomerCategories as useGetAllCustomerTypes,
    // @ts-ignore
    useGetCustomerCategory as useGetCustomerType,
    // @ts-ignore
    useCreateCustomerCategory as useCreateCustomerType,
    // @ts-ignore
    useUpdateCustomerCategory as useUpdateCustomerType,
    // @ts-ignore
    useDeleteCustomerCategory as useDeleteCustomerType
};